import { useState } from 'react';
import { facilityMedRules, facilityMedRulesSave, type ApiError, type MedRuleCredentialView } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { ErrLine, I, chip, seg, sx, useApi, useGuarded } from './kit';

const CAPTION = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin:10px 0 6px;';
const SETTING_LABEL: Record<string, string> = {
  assisted_living: 'assisted living', nursing_facility: 'nursing facility', home_health: 'home health', memory_care: 'memory care',
};
const ROUTE_LABEL: Record<string, string> = {
  injection: 'injections', inhalation: 'inhalation treatments', tube: 'tubes', topical_broken_skin: 'broken skin', oral: 'by mouth',
};

/** One plain line of what a credential may do here. */
export function ruleLine(c: MedRuleCredentialView): string {
  if (!c.mayRecordGiven) return 'May not record a dose';
  const parts: string[] = [];
  if (c.routesDenied.length) parts.push('no ' + c.routesDenied.map((r) => ROUTE_LABEL[r] ?? r).join(', '));
  if (c.routesAllowed !== 'all') parts.push('only ' + c.routesAllowed.join(', '));
  if (c.prn === 'nurse_authorization') parts.push('PRN with a nurse’s authorization');
  if (c.prn === 'denied') parts.push('no PRN');
  if (!c.firstDose) parts.push('no first doses');
  if (c.controlledSchedules.length < 4) parts.push(c.controlledSchedules.length ? 'controlled: Schedule ' + c.controlledSchedules.join(', ') + ' only' : 'no controlled substances');
  if (c.supervision === 'nurse_on_duty_or_on_call') parts.push('a licensed nurse on duty or on call');
  return parts.length ? 'May record doses: ' + parts.join('; ') : 'May record any dose';
}

/**
 * Who may give which medicine in this building (GET /facilities/me/med-rules), read only:
 * the jurisdiction and kind of building, the loaded rule set (researched or reviewed, and the
 * law it cites), or the fail-safe when none is loaded; and the enforcement mode, which the
 * Facility Admin switches (enforce refuses a dose the rules do not allow; warn records it
 * with the rule's warning). Inside Users & Permissions: no page of its own.
 */
export function MedRulesPanel() {
  const { client } = useSession();
  const { wr } = useGuarded();
  const rules = useApi((c, s) => facilityMedRules(c, { params: { id: 'me' }, signal: s }), []);
  const [err, setErr] = useState<ApiError | null>(null);
  const d = rules.data;
  const setMode = async (mode: 'enforce' | 'warn') => {
    setErr(null);
    try {
      await facilityMedRulesSave(client, { params: { id: 'me' }, body: { mode } });
      rules.reload();
    } catch (e) {
      setErr(e as ApiError);
    }
  };
  if (!d) return rules.error ? <ErrLine error={rules.error} /> : null;
  const where = d.jurisdiction ? d.jurisdiction + (d.setting ? ' · ' + (SETTING_LABEL[d.setting] ?? d.setting) : ' · kind of building not set') : 'State not set';
  return (
    <div data-testid="med-rules-panel" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;margin-bottom:12px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;')}>
        <span style={sx('font-size:16px;font-weight:700;')}>Who may give which medicine</span>
        <span style={sx('font-size:16px;color:#54606C;')}>{I(where)}</span>
        {d.ruleSet && (
          <span title={d.ruleSet.status === 'reviewed' ? 'Reviewed by ' + (d.ruleSet.reviewedBy ?? '') : 'Read from the law; not yet reviewed by a person'} style={sx(d.ruleSet.status === 'reviewed' ? chip('#12503C', 'rgba(47,138,111,.16)') : chip('#8A5A00', 'rgba(138,90,0,.12)'))}>
            {I(d.ruleSet.status === 'reviewed' ? 'REVIEWED' : 'RESEARCHED, NOT YET REVIEWED')}
          </span>
        )}
        <span style={sx('flex:1;')} />
        <span role="radiogroup" aria-label="Medication rules mode" style={sx('display:inline-flex;gap:5px;')}>
          {(['enforce', 'warn'] as const).map((m) => (
            <a
              key={m}
              href="#"
              role="radio"
              aria-checked={d.mode === m}
              {...wr(() => {
                if (d.canChangeMode && d.mode !== m) void setMode(m);
              })}
              {...(d.canChangeMode ? (d.mode === m ? { title: 'This building is on ' + (m === 'enforce' ? 'Enforce' : 'Warn only') + ' now.' } : {}) : { 'aria-disabled': true, title: 'Only the Facility Admin changes this.' })}
              style={sx(seg(d.mode === m) + (d.canChangeMode ? (d.mode === m ? 'cursor:default;' : '') : 'opacity:.6;cursor:not-allowed;'))}
            >
              {I(m === 'enforce' ? 'Enforce' : 'Warn only')}
            </a>
          ))}
        </span>
      </div>
      <div style={sx('font-size:16px;color:#54606C;margin-top:6px;line-height:1.5;')}>
        {I(d.mode === 'enforce'
          ? 'A dose these rules do not allow is refused, with the reason and the rule.'
          : 'A dose these rules do not allow is still recorded, with the rule’s warning on the record.')}
      </div>
      {d.failSafe && <div role="status" style={sx('font-size:16px;font-weight:700;color:#8A5A00;margin-top:8px;')}>{I(d.failSafe)}</div>}
      <div style={sx(CAPTION)}>{I(d.ruleSet ? 'Rules · ' + d.ruleSet.id + ' · version ' + d.ruleSet.version + ' · in effect ' + d.ruleSet.effectiveDate : 'Rules · fail-safe')}</div>
      {d.credentials.map((c) => (
        <div key={c.type} style={sx('display:flex;gap:10px;flex-wrap:wrap;row-gap:2px;padding:5px 0;border-top:1px solid rgba(34,39,46,.07);font-size:16px;')}>
          <span style={sx('flex:0 1 230px;min-width:150px;font-weight:700;color:#22272E;')}>{I(c.name)}</span>
          <span style={sx('flex:1 1 260px;color:' + (c.mayRecordGiven ? '#3C454F' : '#B3261E') + ';')}>{I(ruleLine(c))}</span>
        </div>
      ))}
      {d.override != null && <div style={sx('font-size:16px;color:#3C454F;margin-top:6px;')}>{I('This building also has its own stricter rules on top of these.')}</div>}
      {d.ruleSet && (
        <>
          <div style={sx(CAPTION)}>Citations</div>
          {d.ruleSet.citations.map((c) => (
            <div key={c.id} style={sx('font-size:16px;padding:2px 0;')}>
              {c.url ? <a href={c.url} target="_blank" rel="noreferrer" style={sx('color:#2F5C97;font-weight:700;')}>{c.law}</a> : <b>{c.law}</b>}
              {I(' · ' + c.title)}
            </div>
          ))}
        </>
      )}
      <ErrLine error={err} />
    </div>
  );
}
